import { CSSProperties, FC } from 'react';
import * as Diff from 'diff';

export type RowKind = 'insert' | 'delete' | 'update' | 'unchanged';

export interface FieldDiffRow {
  key: string;
  hudLine: string;
  secondary?: string; // rule label, when this row belongs to a rule rather than the HUD line/config itself
  field: string;
  aRaw: unknown;
  bRaw: unknown;
  kind: RowKind;
}

export const rowClassName = (row: FieldDiffRow) => {
  switch (row.kind) {
    case 'insert': return 'bg-green-50';
    case 'delete': return 'bg-red-50';
    case 'unchanged': return 'text-gray-400';
    default: return undefined;
  }
};

// break-word only breaks at "reasonable" points; unbroken strings (GUIDs, JSON keys with no
// spaces) sail past the column edge and overlap the next column. break-all forces a break
// anywhere, which is what a fixed-width table actually needs. Every cell in both diff tables
// (HUD Line Diff and Configuration & Rule Diff) uses this same treatment, via ValueCell/TextCell,
// so no column can regress into the overlap bug independently of the others.
const valueCellStyle: CSSProperties = { whiteSpace: 'normal', wordBreak: 'break-all', overflowWrap: 'anywhere' };
const preStyle: CSSProperties = { ...valueCellStyle, whiteSpace: 'pre-wrap', margin: 0, fontSize: '0.75rem' };

/** Plain-text cell (e.g. the Rule column) with the same wrap-safety as ValueCell. */
export const TextCell: FC<{ value: string }> = ({ value }) => <div style={valueCellStyle}>{value}</div>;

export const humanizeFieldName = (field: string): string =>
  field
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase());

/**
 * Some fields (e.g. FeeRuleExport.expression) are typed `string | null` but actually hold a
 * JSON-serialized object, matching the migration-export convention elsewhere in this codebase.
 * Parse those back to an object so they get pretty-printed/diffed instead of being treated as
 * an opaque scalar string.
 */
function normalizeJsonValue(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    const parsed = JSON.parse(trimmed);
    return typeof parsed === 'object' && parsed !== null ? parsed : value;
  } catch {
    return value;
  }
}

interface DiffLineSegment {
  text: string;
  changed: boolean;
}

/** Diff.diffLines(aJson, bJson): removed = only in A, added = only in B. A chunk can span many
 * lines, so this flattens to one entry per line — keeping only the lines relevant to the side
 * being rendered, flagged for highlighting where they differ. */
function lineSegmentsForSide(aJson: string, bJson: string, side: 'a' | 'b'): DiffLineSegment[] {
  const chunks = Diff.diffLines(aJson, bJson);
  const segments: DiffLineSegment[] = [];
  for (const chunk of chunks) {
    if (side === 'a' && chunk.added) continue;
    if (side === 'b' && chunk.removed) continue;
    const changed = side === 'a' ? !!chunk.removed : !!chunk.added;
    const text = chunk.value.endsWith('\n') ? chunk.value.slice(0, -1) : chunk.value;
    for (const line of text.split('\n')) {
      segments.push({ text: line, changed });
    }
  }
  return segments;
}

const CONTEXT_LINES = 3;

type DisplayLine = { type: 'line'; text: string; changed: boolean } | { type: 'gap' };

/** Keeps every changed line plus CONTEXT_LINES lines of unchanged context around it, collapsing
 * each remaining run of hidden lines into a single gap marker — a unified-diff-style view rather
 * than an abrupt cut straight to the changed lines with no surrounding context. */
function applyContextWindow(lines: DiffLineSegment[]): DisplayLine[] {
  const keep = lines.map((l) => l.changed);
  for (let i = 0; i < lines.length; i++) {
    if (!lines[i].changed) continue;
    for (let j = Math.max(0, i - CONTEXT_LINES); j <= Math.min(lines.length - 1, i + CONTEXT_LINES); j++) {
      keep[j] = true;
    }
  }
  const result: DisplayLine[] = [];
  let i = 0;
  while (i < lines.length) {
    if (keep[i]) {
      result.push({ type: 'line', text: lines[i].text, changed: lines[i].changed });
      i++;
    } else {
      while (i < lines.length && !keep[i]) i++;
      result.push({ type: 'gap' });
    }
  }
  return result;
}

/**
 * Renders one side (A or B) of a field's value. Plain scalars render as text. Object/array
 * values are pretty-printed; when both sides are present and complex, the two are line-diffed
 * and only the lines relevant to this side are shown, with changed lines highlighted — so you
 * see what specifically changed inside a `restrictions`/`settingData`/`expression` blob without
 * a separate JSON viewer.
 */
export const ValueCell: FC<{ aRaw: unknown; bRaw: unknown; side: 'a' | 'b'; jsonDiffsOnly?: boolean }> = ({
  aRaw: aRawIn,
  bRaw: bRawIn,
  side,
  jsonDiffsOnly,
}) => {
  // Local to this component: only ValueCell needs to format/detect-complexity of a value, so
  // these stay private rather than sitting at module scope looking like shared utilities.
  const isComplex = (value: unknown): boolean => typeof value === 'object' && value !== null;
  const prettyJson = (value: unknown): string => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  };
  const simpleFormat = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const aRaw = normalizeJsonValue(aRawIn);
  const bRaw = normalizeJsonValue(bRawIn);
  const raw = side === 'a' ? aRaw : bRaw;
  if (raw === undefined || raw === null || raw === '') {
    return <div style={valueCellStyle}>-</div>;
  }

  const aComplex = isComplex(aRaw);
  const bComplex = isComplex(bRaw);
  if (!aComplex && !bComplex) {
    return <div style={valueCellStyle}>{simpleFormat(raw)}</div>;
  }

  const otherRaw = side === 'a' ? bRaw : aRaw;
  const otherPresent = otherRaw !== undefined && otherRaw !== null && otherRaw !== '';
  if (!otherPresent) {
    // Insert/Delete: nothing to diff against — the whole blob already is the diff, so
    // jsonDiffsOnly has nothing to trim here.
    return <pre style={preStyle}>{prettyJson(raw)}</pre>;
  }

  const aJson = aComplex ? prettyJson(aRaw) : simpleFormat(aRaw);
  const bJson = bComplex ? prettyJson(bRaw) : simpleFormat(bRaw);
  const lineSegments = lineSegmentsForSide(aJson, bJson, side);
  const hasChange = lineSegments.some((l) => l.changed);

  if (jsonDiffsOnly && !hasChange) {
    return <pre style={preStyle}>(no differences)</pre>;
  }

  const displayLines: DisplayLine[] = jsonDiffsOnly
    ? applyContextWindow(lineSegments)
    : lineSegments.map((l) => ({ type: 'line', text: l.text, changed: l.changed }));

  return (
    <pre style={preStyle}>
      {displayLines.map((dl, i) =>
        dl.type === 'gap' ? (
          <span key={i} style={{ display: 'block', color: 'var(--text-color-secondary)', fontStyle: 'italic' }}>
            ⋯
          </span>
        ) : (
          <span
            key={i}
            style={{
              display: 'block',
              backgroundColor: dl.changed ? (side === 'a' ? 'rgba(220,80,80,0.18)' : 'rgba(80,200,120,0.18)') : undefined,
            }}
          >
            {dl.text}
          </span>
        )
      )}
    </pre>
  );
};

/** Every comparable field of a whole new entity is, by definition, new — so Insert emits one row per field. */
export function insertFieldRows<T extends object>(entity: T, fields: (keyof T)[]): Array<{ field: string; aRaw: unknown; bRaw: unknown }> {
  return fields.map((f) => ({ field: humanizeFieldName(f as string), aRaw: null, bRaw: entity[f] }));
}

/** Mirror of insertFieldRows for a whole removed entity. */
export function deleteFieldRows<T extends object>(entity: T, fields: (keyof T)[]): Array<{ field: string; aRaw: unknown; bRaw: unknown }> {
  return fields.map((f) => ({ field: humanizeFieldName(f as string), aRaw: entity[f], bRaw: null }));
}

/** Same field list, both sides equal (Unchanged) — still real field names, not a placeholder. */
export function unchangedFieldRows<T extends object>(entity: T, fields: (keyof T)[]): Array<{ field: string; aRaw: unknown; bRaw: unknown }> {
  return fields.map((f) => ({ field: humanizeFieldName(f as string), aRaw: entity[f], bRaw: entity[f] }));
}

const searchableText = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

/** Matches the search term against every visible/searchable part of a row: HUD Line, Field,
 * Rule (secondary), and both raw values — so searching a value (e.g. "Lender") finds it too,
 * not just column labels. Checks the cheap string fields first so the common case never pays
 * for stringifying aRaw/bRaw (they can be arbitrarily large JSON objects). */
export function matchesRowSearch(row: FieldDiffRow, term: string): boolean {
  if (!term) return true;
  const needle = term.toLowerCase();
  if (
    row.hudLine.toLowerCase().includes(needle) ||
    row.field.toLowerCase().includes(needle) ||
    (row.secondary ?? '').toLowerCase().includes(needle)
  ) {
    return true;
  }
  return searchableText(row.aRaw).toLowerCase().includes(needle) || searchableText(row.bRaw).toLowerCase().includes(needle);
}
